import { Injectable } from '@angular/core'
import { UserReport } from '../../../../src/commons/models/user-report.model'
import { AccountTableItem } from '../../../../src/app/accounts/account-search/models/accountTableItem.model'
import {
  AccountCachePage,
  AccountFilter,
  AccountInfoDto,
} from '../../../../src/app/accounts/account-search/models/accountTableList.model'
import { Org, OrgType } from '../../../../src/commons/models/org.model'
import { Subject } from 'rxjs'
import { ApiService } from '../api/api.service'
import { ConfigSettingKey, OperatorConfigSettingDto } from '../../../../src/commons/models/configSettings.model'
import { UserApiService } from '../../../../src/security/models/services/users/userApi.service'
import { UserDto, AppUserClaims } from '../../../../src/security/models'
import { IColumnConfig } from '../../../../src/data-tables/models'

@Injectable({
  providedIn: 'root',
})
export class CacheService {

  public internalUserOrgChanged = new Subject<boolean>()

  private SLP_USER_INFO = 'slp-user-info'
  private SLP_USER_CLAIMS = 'slp-user-claims'
  private SLP_ACCOUNTS_GRID = 'slp-accounts-grid'
  private SLP_REFERENCE_ID_TRANSACTION_PAGE = 'slp-account-id-transaction-page'
  private SLP_USER_REPORTS = 'slp-reports'
  private SLP_ORG = 'slp-org'
  private SLP_PROGRAM_GROUP_CARD_ART_SETTINGS = 'slp-card-art-settings'

  //persistent local storage
  private SLP_COMMON_CONFIG_SETTINGS = 'p-slp-common-config-settings'
  private SLP_BASE_GRID_COLUMNS = 'p-slp-grid-columns'

  constructor(private userApiService: UserApiService,
    private apiService: ApiService) { }

  public async getUserInfo(): Promise<UserDto> {
    let userInfo = localStorage.getItem(this.SLP_USER_INFO)
    if (userInfo == null) {
      userInfo = JSON.stringify(await this.userApiService.getUserInfo())
      localStorage.setItem(this.SLP_USER_INFO, userInfo)
    }
    return JSON.parse(userInfo) as UserDto
  }

  public async getUserClaims(): Promise<AppUserClaims[]> {
    let userClaims = localStorage.getItem(this.SLP_USER_CLAIMS)
    if (userClaims == null) {
      userClaims = JSON.stringify(await this.userApiService.getUserClaims())
      localStorage.setItem(this.SLP_USER_CLAIMS, userClaims)
    }
    return JSON.parse(userClaims) as AppUserClaims[]
  }

  public resetUserReports(): void {
    localStorage.removeItem(this.SLP_USER_REPORTS)  
  }

  public async getUserReports(): Promise<UserReport[]> {
    let userReports = localStorage.getItem(this.SLP_USER_REPORTS)
    if (userReports == null) {
      userReports = JSON.stringify(await this.apiService.getUserReports())
      localStorage.setItem(this.SLP_USER_REPORTS, userReports)
    }
    return JSON.parse(userReports) as UserReport[]
  }

  public async getProgramGroupCardArtSettings(): Promise<OperatorConfigSettingDto[]> {
    let cardArtSettings = localStorage.getItem(this.SLP_PROGRAM_GROUP_CARD_ART_SETTINGS)
    if (cardArtSettings == null) {
      cardArtSettings = JSON.stringify(await this.apiService.getConfigSettings([ConfigSettingKey.ProgramGroupCardArt]))
      localStorage.setItem(this.SLP_PROGRAM_GROUP_CARD_ART_SETTINGS, cardArtSettings)
    }
    return JSON.parse(cardArtSettings) as OperatorConfigSettingDto[]
  }

  public getAccountPageCache(referenceId: string): AccountCachePage {
    const lastAccount = localStorage.getItem(`${this.SLP_ACCOUNTS_GRID}-${referenceId}`)
    if (lastAccount != null) return JSON.parse(lastAccount) as AccountCachePage
    else return null
  }

  public async getGridColumns(gridName: string): Promise<IColumnConfig[]> {
    const user = await this.getUserInfo()
    const columns = localStorage.getItem(`${this.SLP_BASE_GRID_COLUMNS}_${gridName}_${user.rId}`)
    if (columns != null) return JSON.parse(columns) as IColumnConfig[]
    else return null
  }

  public setGridCollumns = async (gridName: string, columns: IColumnConfig[]): Promise<void> => {
    const user = await this.getUserInfo()
    localStorage.setItem(`${this.SLP_BASE_GRID_COLUMNS}_${gridName}_${user.rId}`, JSON.stringify(columns))
  }

  public setAccountPageCache = (
    referenceId: string,
    accountsGrid: AccountTableItem[],
    filter: AccountFilter,
    fromRecentViewed: boolean,
    recentViewedAccount: AccountInfoDto
  ): void => {
    localStorage.setItem(
      `${this.SLP_ACCOUNTS_GRID}-${referenceId}`,
      JSON.stringify({
        data: accountsGrid,
        filter: filter,
        fromRecentViewed: fromRecentViewed,
        recentViewedAccount: recentViewedAccount,
      } as AccountCachePage)
    )
  }

  public setReferenceIdTransactionPage = (referenceId: string): void =>
    localStorage.setItem(`${this.SLP_REFERENCE_ID_TRANSACTION_PAGE}`, referenceId)

  public getReferenceIdTransactionPage = (): string =>
    localStorage.getItem(`${this.SLP_REFERENCE_ID_TRANSACTION_PAGE}`)

  public setOrg(org: Org): void {
    if (org) localStorage.setItem(this.SLP_ORG, JSON.stringify(org))
  }

  public async getOrg(): Promise<Org> {
    let org: Org
    const cachedOrg = localStorage.getItem(this.SLP_ORG)
    if (cachedOrg != null) {
      org = JSON.parse(cachedOrg) as Org
    }
    else {
      org = await this.apiService.getOrg()
      this.setOrg(org)
    }
    return org
  }

  public setCommonConfigSettings(commonConfigSettings: OperatorConfigSettingDto[]): void {
    localStorage.setItem(this.SLP_COMMON_CONFIG_SETTINGS, JSON.stringify(commonConfigSettings))
  }

  public getCommonConfigSettings(): OperatorConfigSettingDto[] {
    const commonSettings = localStorage.getItem(`${this.SLP_COMMON_CONFIG_SETTINGS}`)
    if (commonSettings != null)
      return JSON.parse(commonSettings) as OperatorConfigSettingDto[]
    else
      return null
  }

  public async isInternalOrg(): Promise<boolean> {
    const org = await this.getOrg()
    return (org && org.type == OrgType.Internal)
  }

  public clearCache(): void {
    const storages = Object.keys(localStorage)
      .filter((x) =>
        x.startsWith('slp'))

    storages.forEach((x) => {
      localStorage.removeItem(x)
    })
  }

}
